/**
 * confirmTheme — shared styled primitives for the one-page public
 * "Confirm & book" flow (components/booking/confirm/*). Built on the
 * rate-gate design-token module so the whole customer surface reads as one
 * product: 4px antd geometry, generous whitespace, visible focus rings,
 * 200ms micro-transitions (reduced-motion aware), 44px tap targets,
 * mobile-first single column that opens into a two-column field grid.
 */
import styled from 'styled-components';
import { Button } from 'antd';
import {
  color,
  radius,
  shadow,
  transition,
  tapTarget,
} from '../../../styles/designTokens';

export const Page = styled.div`
  min-height: 100vh;
  background: ${color.ground};
  padding-bottom: 120px; /* room for the sticky footer bar */
`;

export const PageInner = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 0 16px;
`;

export const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 18px 0 6px;
`;

export const PageTitle = styled.h1`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: ${color.navy};
`;

export const SectionCard = styled.section`
  background: #fff;
  border: 1px solid ${color.line};
  border-radius: ${radius.card};
  box-shadow: ${shadow.card};
  padding: 20px 22px;
  margin-top: 16px;
  @media (max-width: 480px) {
    padding: 16px 14px;
  }
`;

export const SectionHeading = styled.h2`
  margin: 0 0 2px;
  font-size: 15px;
  font-weight: 600;
  color: ${color.navy};
`;

export const SectionHint = styled.p`
  margin: 0 0 14px;
  font-size: 12.5px;
  line-height: 1.55;
  color: ${color.ink2};
`;

/** Two columns from tablet up; single column on phones. */
export const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 16px;
  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

export const FullRow = styled.div`
  grid-column: 1 / -1;
`;

/** Compact uppercase field label (mockup .f-label). */
export const fieldLabelStyle = {
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: color.ink2,
};

/** "from your unlock ✎" affordance on the prefilled email. */
export const PrefillTag = styled.span`
  margin-left: 8px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  color: ${color.primary};
  background: ${color.primarySoft};
  border-radius: 3px;
  padding: 1px 6px;
  white-space: nowrap;
`;

export const PrimaryCta = styled(Button)`
  && {
    height: ${tapTarget};
    min-width: 180px;
    padding: 0 26px;
    border-radius: ${radius.base};
    background: ${color.primary};
    border-color: ${color.primary};
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    transition: ${transition};
  }
  &&:hover,
  &&:focus {
    background: #3aa3f5;
    border-color: #3aa3f5;
    color: #fff;
    box-shadow: 0 0 0 3px rgba(30, 148, 241, 0.18);
  }
  &&[disabled] {
    background: ${color.groundAlt};
    border-color: ${color.line};
    color: ${color.ink2};
  }
  && {
    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }
`;

export const QuietButton = styled(Button)`
  && {
    height: 40px;
    border-radius: ${radius.base};
    border-color: ${color.line};
    color: ${color.ink};
    background: #fff;
    font-size: 13.5px;
    transition: ${transition};
  }
  &&:hover,
  &&:focus {
    border-color: ${color.primary};
    color: ${color.primary};
  }
  && {
    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }
`;

export const FooterBar = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  background: rgba(255, 255, 255, 0.97);
  border-top: 1px solid ${color.line};
  box-shadow: 0 -2px 12px rgba(23, 21, 78, 0.08);
`;

export const FooterInner = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

export const TotalBlock = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.3;
  min-width: 120px;
`;

export const TotalLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${color.ink2};
`;

export const TotalAmount = styled.span`
  font-size: 19px;
  font-weight: 700;
  color: ${color.navy};
  font-variant-numeric: tabular-nums;
`;
